import { Component, computed, effect, ElementRef, inject, input, output } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { Router, RouterLink } from '@angular/router';
import { AiAssistantService } from '../../core/services/ai-assistant.service';
import { AiAssistantI18nModule } from '../../assets/i18n/ai-assistant-i18n.module';
import { Assistant } from '@vendasta/protos/ai_assistants/v1alpha1';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ACCOUNT_GROUP_ID_TOKEN } from '../../core/tokens';
import { of, switchMap } from 'rxjs';
import { AIAssistantSelectDialogComponent } from '../ai-assistant-select-dialog/ai-assistant-select-dialog.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { AiAssistant, DEFAULT_AVATAR_SVG_ICON } from '../../core';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAiIconService } from '@vendasta/galaxy/ai-icon';
import { aiAssistantAssetUrl } from '../../core/pipes/ai-assistant-asset-url.pipe';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'ai-assistant-reference-card',
  imports: [GalaxyAvatarModule, RouterLink, MatButtonModule, AiAssistantI18nModule, MatDialogModule, TranslateModule],
  templateUrl: './ai-assistant-reference-card.component.html',
  styleUrl: './ai-assistant-reference-card.component.scss',
})
export class AiAssistantReferenceCardComponent {
  readonly assistant = input.required<Partial<Assistant>>();
  readonly showSelectAssistantButton = input<boolean>(false);
  readonly selectedAssistant = output<AiAssistant>();
  readonly accountGroupId = toSignal(inject(ACCOUNT_GROUP_ID_TOKEN));
  private readonly aiAssistantService = inject(AiAssistantService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  protected readonly currentUrl = this.router.url;
  protected readonly defaultWorkforce = toSignal(this.aiAssistantService.defaultWorkforce$, { initialValue: [] });
  protected readonly aiIconService = inject(GalaxyAiIconService);

  readonly defaultAvatarIcon = DEFAULT_AVATAR_SVG_ICON;
  private readonly document = inject(DOCUMENT);

  aiAssistant = computed(() =>
    this.aiAssistantService.hydrateAssistantWithDefaultInfo(this.assistant(), this.defaultWorkforce()),
  );
  gradientColor = computed(() => this.aiAssistant()?.decoration?.gradientColor);
  protected readonly avatarSrc = computed(() => {
    const a = this.aiAssistant();
    return (
      a?.assistant?.avatarUrl ||
      a?.decoration?.avatarIconUrl ||
      aiAssistantAssetUrl(a?.decoration?.defaultAvatarIcon, this.document)
    );
  });

  readonly aiAssistantConfigUrl = toSignal(
    toObservable(this.assistant).pipe(
      switchMap((assistant) => {
        const id = assistant?.id;
        return id ? this.aiAssistantService.buildAssistantConfigurationUrl(id) : of('');
      }),
    ),
    { initialValue: '' },
  );

  constructor(private elementRef: ElementRef) {
    const element = this.elementRef.nativeElement;
    effect(() => {
      element.style.setProperty('--gradient-start-color', this.gradientColor());
    });
  }

  openSelectAssistantDialog() {
    this.dialog
      .open(AIAssistantSelectDialogComponent, {
        width: '800px',
        data: { activeAssistantId: this.assistant()?.id },
      })
      .afterClosed()
      .subscribe((result: AiAssistant | null) => {
        if (result) {
          this.selectedAssistant.emit(result);
        }
      });
  }
}
